import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Grid,
  TextField,
  Alert,
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Print as PrintIcon,
  Download as DownloadIcon,
  Edit as EditIcon,
  Close as CloseIcon,
  LocalPharmacy as PharmacyIcon,
  Science as LabIcon,
  Description as DescriptionIcon,
  Visibility as VisibilityIcon,
  Share as ShareIcon,
} from '@mui/icons-material';
import { Prescription, PrescriptionPharmacyDelivery } from '../../types';
import { api } from '../../lib/api';
import { usePermissions } from '../../contexts/PermissionContext';
import { PERM } from '../../lib/permissions';
import { safeConsole } from '../../lib/piiProtection';
import { handlePrescriptionPdf } from '../../lib/pdfUtils';
import { formatDateTime } from '../../lib/utils';
import { PrescriptionShareDialog } from './PrescriptionShareDialog';
import { PharmacySelector } from './PharmacySelector';
import { LabSelector } from './LabSelector';
import type { MedicationSendDecision } from '../../lib/api/modules/prescriptions';

interface PrescriptionViewDialogProps {
  open: boolean;
  onClose: () => void;
  prescription: Prescription;
  onUpdate?: () => void;
  onEdit?: () => void;
}

const PrescriptionViewDialog: React.FC<PrescriptionViewDialogProps> = ({
  open,
  onClose,
  prescription,
  onUpdate,
  onEdit,
}) => {
  const { can, isSuperAdmin } = usePermissions();
  // Creating/issuing/dispensing a prescription is a clinical write — gate on OPD_PRESCRIPTIONS_CREATE.
  const canManageRx = isSuperAdmin || can(PERM.OPD_PRESCRIPTIONS_CREATE);
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [issuing, setIssuing] = useState(false);
  const [canceling, setCanceling] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [printing, setPrinting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showShareDialog, setShowShareDialog] = useState(false);
  
  // Pharmacy states
  const [showPharmacyDialog, setShowPharmacyDialog] = useState(false);
  const [selectedPharmacyId, setSelectedPharmacyId] = useState('');
  const [pharmacyNotes, setPharmacyNotes] = useState('');
  const [sendingToPharmacy, setSendingToPharmacy] = useState(false);
  const [pharmacyDelivery, setPharmacyDelivery] = useState<PrescriptionPharmacyDelivery | null>(null);
  const [medicationDecisions, setMedicationDecisions] = useState<MedicationSendDecision[]>([]);
  const [_loadingDelivery, setLoadingDelivery] = useState(false);

  // Lab states
  const [showLabDialog, setShowLabDialog] = useState(false);
  const [selectedLabId, setSelectedLabId] = useState('');
  const [assigningLab, setAssigningLab] = useState(false);

  useEffect(() => {
    if (open && prescription.status === 'ISSUED') {
      loadPharmacyDelivery();
    }
  }, [open, prescription.id, prescription.status]);

  const loadPharmacyDelivery = async () => {
    try {
      setLoadingDelivery(true);
      const response = await api.prescriptions.getPharmacyDelivery(prescription.id);
      setPharmacyDelivery(response.data || null);
    } catch (err) {
      // Ignore error if no delivery found
      setPharmacyDelivery(null);
    } finally {
      setLoadingDelivery(false);
    }
  };

  const handleOpenPharmacyDialog = () => {
    setShowPharmacyDialog(true);
    setError(null);
  };

  const handleSendToPharmacy = async () => {
    if (!selectedPharmacyId) {
      setError('Please select a pharmacy');
      return;
    }

    try {
      setSendingToPharmacy(true);
      setError(null);
      await api.prescriptions.sendToPharmacy(prescription.id, selectedPharmacyId, pharmacyNotes, medicationDecisions);
      await loadPharmacyDelivery();
      setShowPharmacyDialog(false);
      setSelectedPharmacyId('');
      setPharmacyNotes('');
    } catch (err: any) {
      setError(err.message || 'Failed to send prescription to pharmacy');
    } finally {
      setSendingToPharmacy(false);
    }
  };

  const handleOpenLabDialog = () => {
    setShowLabDialog(true);
    setError(null);
  };

  const handleAssignLab = async () => {
    if (!selectedLabId) {
      setError('Please select a lab');
      return;
    }

    try {
      setAssigningLab(true);
      setError(null);
      await api.labs.assignLab(prescription.id, selectedLabId);
      setShowLabDialog(false);
      setSelectedLabId('');
    } catch (err: any) {
      setError(err.message || 'Failed to assign lab');
    } finally {
      setAssigningLab(false);
    }
  };

  const getDeliveryStatusColor = (status: string) => {
    switch (status) {
      case 'SENT_TO_PHARMACY':
        return 'info';
      case 'VIEWED':
        return 'primary';
      case 'PREPARED':
        return 'warning';
      case 'COLLECTED':
        return 'success';
      case 'CANCELLED':
        return 'error';
      default:
        return 'default';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'DRAFT':
        return 'warning';
      case 'ISSUED':
        return 'success';
      case 'CANCELLED':
        return 'error';
      default:
        return 'default';
    }
  };

  const handleIssue = async () => {
    try {
      setIssuing(true);
      setError(null);
      await api.prescriptions.issue(prescription.id);
      onUpdate?.();
      onClose();
    } catch (err: any) {
      setError(err.message || 'Failed to issue prescription');
    } finally {
      setIssuing(false);
    }
  };

  const handleCancelSubmit = async () => {
    try {
      setCanceling(true);
      setError(null);
      await api.prescriptions.cancel(prescription.id, cancelReason);
      onUpdate?.();
      setShowCancelDialog(false);
      onClose();
    } catch (err: any) {
      setError(err.message || 'Failed to cancel prescription');
    } finally {
      setCanceling(false);
    }
  };

  const handlePrint = async () => {
    try {
      setPrinting(true);
      setError(null);
      
      const result = await handlePrescriptionPdf({
        prescriptionId: prescription.id,
        action: 'print',
        patientName: prescription.patientName,
        prescriptionDate: prescription.status === 'ISSUED' ? (prescription.issuedAt || prescription.createdAt) : prescription.createdAt,
      });
      
      if (!result.success) {
        setError(result.error || 'Failed to load prescription for printing');
      }
    } catch (err) {
      setError('Failed to load prescription for printing');
      safeConsole.error('Print error:', err);
    } finally {
      setPrinting(false);
    }
  };

  const handleEdit = () => {
    onEdit?.();
  };

  const handleDownloadPdf = async () => {
    try {
      setDownloading(true);
      setError(null);
      
      const result = await handlePrescriptionPdf({
        prescriptionId: prescription.id,
        action: 'download',
        patientName: prescription.patientName,
        prescriptionDate: prescription.status === 'ISSUED' ? (prescription.issuedAt || prescription.createdAt) : prescription.createdAt,
      });
      
      if (!result.success) {
        setError(result.error || 'Failed to download prescription PDF');
      }
    } catch (err) {
      setError('Failed to download prescription PDF');
    } finally {
      setDownloading(false);
    }
  };

  const handleViewDocument = async (filePath: string) => {
    try {
      const response = await api.labUpload.getPreviewUrl(filePath);
      if (response.data?.url) {
        window.open(response.data.url, '_blank');
      } else {
        throw new Error('Failed to get preview URL');
      }
    } catch (err) {
      safeConsole.error('Preview error:', err);
      setError('Failed to open document. Please try again.');
    }
  };

  if (showCancelDialog) {
    return (
      <Dialog open={open} onClose={() => !canceling && setShowCancelDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Cancel Prescription</DialogTitle>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Are you sure you want to cancel this prescription? This action cannot be undone.
          </Typography>
          <TextField
            fullWidth
            label="Cancellation Reason (Optional)"
            multiline
            rows={3}
            value={cancelReason}
            onChange={(e) => setCancelReason(e.target.value)}
            placeholder="Enter reason for cancellation..."
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowCancelDialog(false)} disabled={canceling}>
            Back
          </Button>
          <Button
            onClick={handleCancelSubmit}
            color="error"
            variant="contained"
            disabled={canceling}
          >
            {canceling ? 'Canceling...' : 'Cancel Prescription'}
          </Button>
        </DialogActions>
      </Dialog>
    );
  }

  if (showPharmacyDialog) {
    return (
      <Dialog open={open} onClose={() => !sendingToPharmacy && setShowPharmacyDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Send Prescription to Pharmacy</DialogTitle>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Select a pharmacy to send this prescription digitally. The pharmacy will be notified and can prepare the medicines.
          </Typography>
          
          <Box sx={{ mb: 2 }}>
            <PharmacySelector
              selectedPharmacyId={selectedPharmacyId}
              onSelectPharmacy={setSelectedPharmacyId}
              prescriptionId={prescription.id}
              medications={(prescription.medications ?? [])
                .filter((m): m is typeof m & { id: string } => Boolean(m.id))
                .map((m) => ({ id: m.id, name: m.medicationName }))}
              onAvailabilityChange={setMedicationDecisions}
            />
          </Box>

          <TextField
            fullWidth
            label="Notes for Pharmacy (Optional)"
            multiline
            rows={3}
            value={pharmacyNotes}
            onChange={(e) => setPharmacyNotes(e.target.value)}
            placeholder="Any special instructions for the pharmacy..."
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowPharmacyDialog(false)} disabled={sendingToPharmacy}>
            Cancel
          </Button>
          <Button
            onClick={handleSendToPharmacy}
            color="primary"
            variant="contained"
            disabled={sendingToPharmacy || !selectedPharmacyId}
            startIcon={<PharmacyIcon />}
          >
            {sendingToPharmacy ? 'Sending...' : 'Send to Pharmacy'}
          </Button>
        </DialogActions>
      </Dialog>
    );
  }

  if (showLabDialog) {
    return (
      <Dialog open={open} onClose={() => !assigningLab && setShowLabDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Send Lab Tests to Lab</DialogTitle>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Select which lab this prescription's lab tests should be routed to.
          </Typography>

          <Box sx={{ mb: 2 }}>
            <LabSelector selectedLabId={selectedLabId} onSelectLab={setSelectedLabId} />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowLabDialog(false)} disabled={assigningLab}>
            Cancel
          </Button>
          <Button
            onClick={handleAssignLab}
            color="primary"
            variant="contained"
            disabled={assigningLab || !selectedLabId}
          >
            {assigningLab ? 'Sending...' : 'Send to Lab'}
          </Button>
        </DialogActions>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box display="flex" alignItems="center" gap={2}>
            <Typography variant="h6">Prescription Details</Typography>
            <Chip
              label={prescription.status}
              color={getStatusColor(prescription.status)}
              size="small"
            />
            {pharmacyDelivery && (
              <Chip
                icon={<PharmacyIcon />}
                label={pharmacyDelivery.status.replace(/_/g, ' ')}
                color={getDeliveryStatusColor(pharmacyDelivery.status)}
                size="small"
              />
            )}
          </Box>
          <Button onClick={onClose} size="small">
            <CloseIcon />
          </Button>
        </Box>
      </DialogTitle>

      <DialogContent dividers sx={{ padding: { xs: 2, sm: 3 } }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Paper elevation={0} sx={{ p: 2, bgcolor: 'grey.50' }}>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">
                    Patient
                  </Typography>
                  <Typography variant="body1" fontWeight={500}>
                    {prescription.patientName}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">
                    Doctor
                  </Typography>
                  <Typography variant="body1" fontWeight={500}>
                    {prescription.doctorName}
                  </Typography>
                </Grid>
                {prescription.templateName && (
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">
                      Template
                    </Typography>
                    <Typography variant="body1" fontWeight={500}>
                      {prescription.templateName}
                    </Typography>
                  </Grid>
                )}
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">
                    Created
                  </Typography>
                  <Typography variant="body1">
                    {formatDateTime(prescription.createdAt)}
                  </Typography>
                </Grid>
                {prescription.issuedAt && (
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">
                      Issued
                    </Typography>
                    <Typography variant="body1">
                      {formatDateTime(prescription.issuedAt)}
                    </Typography>
                  </Grid>
                )}
                {prescription.cancelledAt && (
                  <>
                    <Grid item xs={6}>
                      <Typography variant="caption" color="text.secondary">
                        Cancelled
                      </Typography>
                      <Typography variant="body1">
                        {formatDateTime(prescription.cancelledAt)}
                      </Typography>
                    </Grid>
                    {prescription.cancellationReason && (
                      <Grid item xs={12}>
                        <Typography variant="caption" color="text.secondary">
                          Cancellation Reason
                        </Typography>
                        <Typography variant="body1">
                          {prescription.cancellationReason}
                        </Typography>
                      </Grid>
                    )}
                  </>
                )}
              </Grid>
            </Paper>
          </Grid>

          {pharmacyDelivery && (
            <Grid item xs={12}>
              <Paper elevation={0} sx={{ p: 2, bgcolor: 'primary.50', border: '1px solid', borderColor: 'primary.200' }}>
                <Box display="flex" alignItems="center" gap={1} mb={1}>
                  <PharmacyIcon color="primary" />
                  <Typography variant="subtitle2" fontWeight={600}>
                    Pharmacy Delivery Status
                  </Typography>
                </Box>
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">
                      Pharmacy
                    </Typography>
                    <Typography variant="body2" fontWeight={500}>
                      {pharmacyDelivery.pharmacyName}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">
                      Status
                    </Typography>
                    <Box>
                      <Chip
                        label={pharmacyDelivery.status.replace(/_/g, ' ')}
                        color={getDeliveryStatusColor(pharmacyDelivery.status)}
                        size="small"
                      />
                    </Box>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">
                      Sent At
                    </Typography>
                    <Typography variant="body2">
                      {formatDateTime(pharmacyDelivery.sentAt)}
                    </Typography>
                  </Grid>
                  {pharmacyDelivery.preparedAt && (
                    <Grid item xs={6}>
                      <Typography variant="caption" color="text.secondary">
                        Prepared At
                      </Typography>
                      <Typography variant="body2">
                        {formatDateTime(pharmacyDelivery.preparedAt)}
                      </Typography>
                    </Grid>
                  )}
                  {pharmacyDelivery.collectedAt && (
                    <Grid item xs={6}>
                      <Typography variant="caption" color="text.secondary">
                        Collected At
                      </Typography>
                      <Typography variant="body2">
                        {formatDateTime(pharmacyDelivery.collectedAt)}
                      </Typography>
                    </Grid>
                  )}
                  {pharmacyDelivery.notes && (
                    <Grid item xs={12}>
                      <Typography variant="caption" color="text.secondary">
                        Notes
                      </Typography>
                      <Typography variant="body2">
                        {pharmacyDelivery.notes}
                      </Typography>
                    </Grid>
                  )}
                </Grid>
              </Paper>
            </Grid>
          )}

          {prescription.chiefComplaint && (
            <Grid item xs={12}>
              <Typography variant="subtitle2" gutterBottom>
                Chief Complaint
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {prescription.chiefComplaint}
              </Typography>
            </Grid>
          )}

          {prescription.diagnosis && (
            <Grid item xs={12}>
              <Typography variant="subtitle2" gutterBottom>
                Diagnosis
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {prescription.diagnosis}
              </Typography>
            </Grid>
          )}

          {prescription.clinicalNotes && (
            <Grid item xs={12}>
              <Typography variant="subtitle2" gutterBottom>
                Clinical Notes
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {prescription.clinicalNotes}
              </Typography>
            </Grid>
          )}

          {prescription.medications && prescription.medications.length > 0 && (
            <Grid item xs={12}>
              <Typography variant="subtitle2" gutterBottom>
                Medications
              </Typography>
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Medication</TableCell>
                      <TableCell>Dosage</TableCell>
                      <TableCell>Frequency</TableCell>
                      <TableCell>Duration</TableCell>
                      <TableCell>Instructions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {prescription.medications.map((med, index) => (
                      <TableRow key={med.id || index}>
                        <TableCell>{med.medicationName}</TableCell>
                        <TableCell>{med.dosage}</TableCell>
                        <TableCell>{med.frequency}</TableCell>
                        <TableCell>{med.duration}</TableCell>
                        <TableCell>{med.instructions || '-'}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Grid>
          )}

          {prescription.labTests && prescription.labTests.length > 0 && (
            <Grid item xs={12}>
              <Typography variant="subtitle2" gutterBottom>
                Lab Tests
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {prescription.labTests.map((test, index) => (
                  <Chip key={index} label={test} size="small" />
                ))}
              </Box>
            </Grid>
          )}

          {prescription.labDocuments && prescription.labDocuments.length > 0 && (
            <Grid item xs={12}>
              <Typography variant="subtitle2" gutterBottom>
                Attached Lab Documents
              </Typography>
              <Paper variant="outlined" sx={{ p: 0 }}>
                <Table size="small">
                  <TableBody>
                    {prescription.labDocuments.map((doc) => (
                      <TableRow key={doc.id}>
                        <TableCell sx={{ display: 'flex', alignItems: 'center', gap: 1, borderBottom: 'none' }}>
                          <DescriptionIcon color="action" fontSize="small" />
                          <Typography variant="body2">{doc.fileName}</Typography>
                        </TableCell>
                        <TableCell align="right" sx={{ borderBottom: 'none' }}>
                          <Button
                            size="small"
                            startIcon={<VisibilityIcon />}
                            onClick={() => handleViewDocument(doc.filePath)}
                          >
                            View
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Paper>
            </Grid>
          )}

          {prescription.followUpDays && (
            <Grid item xs={12}>
              <Typography variant="subtitle2" gutterBottom>
                Follow-up
              </Typography>
              <Typography variant="body2" color="text.secondary">
                After {prescription.followUpDays} days
              </Typography>
              {prescription.followUpInstructions && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  {prescription.followUpInstructions}
                </Typography>
              )}
            </Grid>
          )}
        </Grid>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          {prescription.status === 'DRAFT' && onEdit && canManageRx && (
            <Button
              startIcon={<EditIcon />}
              onClick={handleEdit}
              variant="outlined"
            >
              Edit
            </Button>
          )}

          <Button
            startIcon={<PrintIcon />}
            onClick={handlePrint}
            variant="outlined"
            disabled={printing}
          >
            {printing ? 'Loading...' : 'Print'}
          </Button>

          <Button
            startIcon={<DownloadIcon />}
            onClick={handleDownloadPdf}
            variant="outlined"
            disabled={downloading}
          >
            {downloading ? 'Downloading...' : 'Download PDF'}
          </Button>

          {prescription.status === 'ISSUED' && !pharmacyDelivery && canManageRx && (
            <Button
              startIcon={<PharmacyIcon />}
              onClick={handleOpenPharmacyDialog}
              color="primary"
              variant="contained"
            >
              Send to Pharmacy
            </Button>
          )}

          {prescription.status === 'ISSUED' && canManageRx && (prescription.labTests?.length ?? 0) > 0 && (
            <Button
              startIcon={<LabIcon />}
              onClick={handleOpenLabDialog}
              color="primary"
              variant="outlined"
            >
              Send to Lab
            </Button>
          )}

          {prescription.status === 'ISSUED' && canManageRx && (
            <Button
              startIcon={<ShareIcon />}
              onClick={() => setShowShareDialog(true)}
              color="secondary"
              variant="outlined"
            >
              Share with Patient
            </Button>
          )}

          {prescription.status === 'DRAFT' && canManageRx && (
            <>
              <Button
                startIcon={<CancelIcon />}
                onClick={() => setShowCancelDialog(true)}
                color="error"
                variant="outlined"
              >
                Cancel
              </Button>

              <Button
                startIcon={<CheckCircleIcon />}
                onClick={handleIssue}
                color="success"
                variant="contained"
                disabled={issuing}
              >
                {issuing ? 'Issuing...' : 'Issue to Patient'}
              </Button>
            </>
          )}

          <Button onClick={onClose}>Close</Button>
        </Box>
      </DialogActions>

      <PrescriptionShareDialog
        open={showShareDialog}
        onClose={() => setShowShareDialog(false)}
        prescriptionId={prescription.id}
      />
    </Dialog>
  );
};

export default PrescriptionViewDialog;
